/**
 * Shades that compare arms on one value: the best arm green, the worst red.
 */

export const rate = (passed: number, graded: number): number | null => (graded === 0 ? null : passed / graded);

/** From best to worst. The middle one is neutral. */
const SHADES = ['bg-green-100 text-green-800', 'bg-green-50 text-green-700', 'text-gray-700', 'bg-red-50 text-red-700', 'bg-red-100 text-red-800'];

/** Time and cost within this share of each other count as equal. */
export const TOLERANCE = 0.05;

interface ShadeOptions {
  /** Values that show the same share a rank, so equal-looking cells never differ in shade. */
  format?: (value: number) => string;
  /** Values within this share of the best value of their rank join that rank. */
  tolerance?: number;
}

/**
 * A shade per arm by rank: the best value green, the worst red, the others in between. By
 * rank, so one outlier does not give close values the same shade. No shade when all values
 * share one rank.
 */
export function shades(values: (number | null)[], better: 'higher' | 'lower', { format = String, tolerance = 0 }: ShadeOptions = {}): (string | undefined)[] {
  const known = values.filter((value): value is number => value !== null).sort((a, b) => (better === 'higher' ? b - a : a - b));
  const ranks = new Map<string, number>();
  let anchor: number | undefined;
  let rank = -1;
  known.forEach((value) => {
    const tied = anchor !== undefined && (format(value) === format(anchor) || Math.abs(value - anchor) <= tolerance * Math.max(Math.abs(anchor), Math.abs(value)));
    if (!tied) {
      anchor = value;
      rank++;
    }
    ranks.set(format(value), rank);
  });
  if (rank < 1) return values.map(() => undefined);
  return values.map((value) => (value === null ? undefined : SHADES[Math.round(((ranks.get(format(value)) ?? 0) / rank) * (SHADES.length - 1))]));
}
